import { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  Camera, Car, User, MapPin, CheckCircle2, ArrowRight, ArrowLeft,
  Upload, X, AlertTriangle, Loader2, Phone, Building2, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import { LogoBlue } from '../components/Logo';

const STEPS = [
  { id: 1, title: 'Identité', icon: Phone },
  { id: 2, title: 'Photos', icon: Camera },
  { id: 3, title: 'Véhicules', icon: Car },
  { id: 4, title: 'Tiers', icon: User },
  { id: 5, title: 'Incident', icon: MapPin },
  { id: 6, title: 'Envoi', icon: CheckCircle2 },
];

const DriverDeclare = () => {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedRef, setSubmittedRef] = useState('');

  // Step 1: Identity
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [depotManagerName, setDepotManagerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyValid, setCompanyValid] = useState(false);
  const [constatSigned, setConstatSigned] = useState(null); // null = not answered, true/false

  // Step 2: Photos
  const [photos, setPhotos] = useState([]);
  const fileInputRef = useRef(null);

  // Step 3: Vehicles
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [tpVehiclePlate, setTpVehiclePlate] = useState('');

  // Step 4: Third party
  const [tpFirstName, setTpFirstName] = useState('');
  const [tpLastName, setTpLastName] = useState('');
  const [tpEmail, setTpEmail] = useState('');
  const [tpPhone, setTpPhone] = useState('');
  const [tpAddress, setTpAddress] = useState('');

  // Step 5: Incident
  const [incidentDate, setIncidentDate] = useState(new Date().toISOString().split('T')[0]);
  const [incidentTime, setIncidentTime] = useState(new Date().toTimeString().slice(0, 5));
  const [description, setDescription] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [notes, setNotes] = useState('');

  const validateCompany = async () => {
    if (!companyName.trim()) {
      setError('Veuillez renseigner le nom de la société');
      return false;
    }
    if (!driverName.trim() && !depotManagerName.trim()) {
      setError('Veuillez renseigner au moins un nom (chauffeur ou chef de dépôt)');
      return false;
    }
    if (constatSigned === null) {
      setError('Veuillez indiquer si un constat a été rempli et signé');
      return false;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/public/validate-company`, { company_name: companyName.trim() });
      setCompanyValid(true);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Société non reconnue');
      setCompanyValid(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Persist form state in sessionStorage (survives Android PWA restart)
  useEffect(() => {
    const saved = sessionStorage.getItem('c2aa_draft');
    if (saved) {
      try {
        const d = JSON.parse(saved);
        setDriverName(d.driverName || '');
        setDriverPhone(d.driverPhone || '');
        setCompanyName(d.companyName || '');
        setVehiclePlate(d.vehiclePlate || '');
        setTpVehiclePlate(d.tpVehiclePlate || '');
        setTpFirstName(d.tpFirstName || '');
        setTpLastName(d.tpLastName || '');
        setTpEmail(d.tpEmail || '');
        setTpPhone(d.tpPhone || '');
        setTpAddress(d.tpAddress || '');
        setIncidentDate(d.incidentDate || new Date().toISOString().split('T')[0]);
        setIncidentTime(d.incidentTime || new Date().toTimeString().slice(0, 5));
        setDescription(d.description || '');
        setLocationAddress(d.locationAddress || '');
        setNotes(d.notes || '');
        if (d.started) { setStarted(true); setStep(d.step || 1); }
        if (d.companyValid) setCompanyValid(true);
      } catch (e) { /* ignore */ }
    }
  }, []);

  // Save draft on every state change
  useEffect(() => {
    if (started && !submitted) {
      sessionStorage.setItem('c2aa_draft', JSON.stringify({
        started, step, driverName, driverPhone, companyName, companyValid,
        vehiclePlate, tpVehiclePlate, tpFirstName, tpLastName, tpEmail, tpPhone, tpAddress,
        incidentDate, incidentTime, description, locationAddress, notes
      }));
    }
  }, [started, step, driverName, driverPhone, companyName, companyValid,
      vehiclePlate, tpVehiclePlate, tpFirstName, tpLastName, tpEmail, tpPhone, tpAddress,
      incidentDate, incidentTime, description, locationAddress, notes, submitted]);

  const handlePhotoAdd = (e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      
      files.forEach(file => {
        // Accept images + files with empty MIME (Samsung camera returns empty type sometimes)
        const isImage = !file.type || file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name);
        if (!isImage) return;
        const preview = URL.createObjectURL(file);
        setPhotos(prev => [...prev, { id: Date.now() + Math.random(), file, preview }]);
      });
    } catch (err) {
      console.error('Photo add error:', err);
      toast.error('Erreur lors de l\'ajout de la photo. Réessayez.');
    }
    try { e.target.value = ''; } catch (err) { /* */ }
  };

  const nextStep = async () => {
    setError('');
    if (step === 1) {
      const valid = await validateCompany();
      if (!valid) return;
    }
    if (step === 2 && photos.length === 0) {
      // Photos optionnelles - Samsung PWA ne supporte pas bien la caméra
      // Le chauffeur peut continuer sans photos et les ajouter depuis la galerie
    }
    if (step === 3 && !vehiclePlate.trim()) {
      setError('Plaque d\'immatriculation requise');
      return;
    }
    if (step === 4) {
      if (!tpFirstName.trim() || !tpLastName.trim() || !tpEmail.trim() || !tpPhone.trim()) {
        setError('Prénom, nom, email et téléphone du tiers sont obligatoires');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(tpEmail)) {
        setError('Email du tiers invalide');
        return;
      }
    }
    if (step === 5 && !description.trim()) {
      setError('Décrivez l\'incident');
      return;
    }
    setStep(prev => Math.min(prev + 1, 6));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('company_name', companyName.trim());
      formData.append('driver_name', driverName.trim());
      formData.append('depot_manager_name', depotManagerName.trim());
      formData.append('constat_signed', constatSigned ? 'true' : 'false');
      formData.append('incident_date', new Date(`${incidentDate}T${incidentTime}`).toISOString());
      formData.append('incident_description', description);
      formData.append('location_address', locationAddress);
      formData.append('vehicle_plate', vehiclePlate.trim());
      formData.append('tp_first_name', tpFirstName.trim());
      formData.append('tp_last_name', tpLastName.trim());
      formData.append('tp_email', tpEmail.trim());
      formData.append('tp_phone', tpPhone.trim());
      formData.append('tp_address', tpAddress.trim());
      formData.append('tp_vehicle_plate', tpVehiclePlate.trim());
      formData.append('driver_notes', notes);

      for (const photo of photos) {
        formData.append('photos', photo.file);
      }

      toast.info('Envoi de votre déclaration en cours...');
      const res = await axios.post(`${API_URL}/public/declare`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });

      setSubmittedRef(res.data.reference);
      setSubmitted(true);
      sessionStorage.removeItem('c2aa_draft');
      toast.success('Déclaration envoyée !');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // Landing screen
  if (!started) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="driver-landing">
        <header className="p-4 flex justify-center border-b border-slate-100">
          <LogoBlue />
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
          <div className="w-20 h-20 rounded-full bg-[#d4a52a]/10 flex items-center justify-center mb-6">
            <Car className="w-10 h-10 text-[#d4a52a]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1e3a5f] text-center mb-2">C2 AMIABLE</h1>
          <p className="text-slate-500 text-center mb-10 text-sm">Résolution amiable de sinistres automobiles</p>
          <Button
            onClick={() => setStarted(true)}
            className="w-full max-w-sm h-16 text-lg bg-[#d4a52a] hover:bg-[#c49a25] text-white rounded-2xl shadow-lg"
            data-testid="start-declare-btn"
          >
            <AlertTriangle className="w-6 h-6 mr-3" />
            Déclarer un sinistre
          </Button>
          <p className="text-xs text-slate-400 mt-6 text-center">
            En cas de dommage corporel, appelez le 15 (SAMU) ou le 18 (Pompiers)
          </p>
        </div>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="declare-success">
        <header className="p-4 flex justify-center border-b border-slate-100">
          <LogoBlue />
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#1e3a5f] text-center mb-2">Déclaration envoyée</h2>
          <p className="text-slate-600 text-center mb-2">Référence : <strong>{submittedRef}</strong></p>
          <p className="text-sm text-slate-500 text-center mb-8 max-w-sm">
            Un email a été envoyé au tiers. Votre gestionnaire C2 Assurances va traiter votre dossier.
          </p>
          <p className="text-sm text-slate-500 text-center mb-8">
            Contact : <strong>04 98 09 28 70</strong>
          </p>
          <Button
            onClick={() => {
              sessionStorage.removeItem('c2aa_draft');
              setStarted(false); setStep(1); setSubmitted(false);
              setPhotos([]); setDescription(''); setNotes(''); setDriverName('');
              setDepotManagerName(''); setConstatSigned(null);
              setTpFirstName(''); setTpLastName(''); setTpEmail(''); setTpPhone(''); setTpAddress('');
              setVehiclePlate(''); setTpVehiclePlate(''); setCompanyValid(false);
            }}
            variant="outline"
            className="w-full max-w-sm h-12"
            data-testid="new-declare-btn"
          >
            Nouvelle déclaration
          </Button>
        </div>
      </div>
    );
  }

  const progress = (step / 6) * 100;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="driver-declare">
      <header className="bg-white p-4 flex items-center justify-between border-b border-slate-100">
        <LogoBlue />
        <span className="text-xs text-slate-400">Étape {step}/6</span>
      </header>

      <Progress value={progress} className="h-1" />

      <div className="flex-1 px-4 py-6 pb-32 max-w-lg mx-auto w-full">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Identity */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-identity">
            <div className="text-center mb-4">
              <Phone className="w-10 h-10 text-[#d4a52a] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-[#1e3a5f]">Informations</h2>
            </div>

            {/* Société */}
            <div className="space-y-2">
              <Label>Nom de la société *</Label>
              <Input
                type="text"
                placeholder="Société..."
                value={companyName}
                onChange={(e) => { setCompanyName(e.target.value); setCompanyValid(false); }}
                className="h-12 text-lg"
                data-testid="company-name-input"
              />
            </div>

            {/* Chauffeur de bus */}
            <Card className="border-[#1e3a5f]/20">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-2 text-[#1e3a5f] font-semibold">
                  <User className="w-5 h-5" />
                  <span>Chauffeur de bus</span>
                </div>
                <div className="space-y-2">
                  <Label>Nom et prénom</Label>
                  <Input
                    type="text"
                    placeholder="Jean Dupont"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    className="h-12 text-lg"
                    data-testid="driver-name-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Chef de dépôt */}
            <Card className="border-[#1e3a5f]/20">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-2 text-[#1e3a5f] font-semibold">
                  <Building2 className="w-5 h-5" />
                  <span>Chef de dépôt</span>
                </div>
                <div className="space-y-2">
                  <Label>Nom et prénom</Label>
                  <Input
                    type="text"
                    placeholder="Marie Martin"
                    value={depotManagerName}
                    onChange={(e) => setDepotManagerName(e.target.value)}
                    className="h-12 text-lg"
                    data-testid="depot-manager-input"
                  />
                </div>
              </CardContent>
            </Card>

            <p className="text-xs text-slate-500 text-center">
              * Au moins un nom est requis (chauffeur ou chef de dépôt)
            </p>

            {/* Question Constat */}
            <Card className={`border-2 ${constatSigned === null ? 'border-amber-300 bg-amber-50' : constatSigned ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
              <CardContent className="pt-4 space-y-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${constatSigned === null ? 'text-amber-600' : constatSigned ? 'text-emerald-600' : 'text-red-600'}`} />
                  <p className="font-medium text-slate-800">
                    Un constat d'assurance a été rempli et signé des deux parties ?
                  </p>
                </div>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setConstatSigned(true)}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                      constatSigned === true 
                        ? 'bg-emerald-600 text-white shadow-md' 
                        : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-emerald-400'
                    }`}
                    data-testid="constat-yes-btn"
                  >
                    ✓ Oui
                  </button>
                  <button
                    type="button"
                    onClick={() => setConstatSigned(false)}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                      constatSigned === false 
                        ? 'bg-red-600 text-white shadow-md' 
                        : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-red-400'
                    }`}
                    data-testid="constat-no-btn"
                  >
                    ✗ Non
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Photos */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-photos">
            <div className="text-center mb-4">
              <Camera className="w-10 h-10 text-[#d4a52a] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-[#1e3a5f]">Photos des dommages</h2>
              <p className="text-sm text-slate-500">Ajoutez des photos des véhicules</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {photos.map((p) => (
                <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border-2 border-slate-200">
                  <img src={p.preview} alt="Photo" className="w-full h-full object-cover" />
                  <button
                    onClick={() => {
                      URL.revokeObjectURL(p.preview);
                      setPhotos(prev => prev.filter(x => x.id !== p.id));
                    }}
                    className="absolute top-1 right-1 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-xl border-2 border-dashed border-[#d4a52a] flex flex-col items-center justify-center text-[#d4a52a] hover:bg-[#d4a52a]/5"
                data-testid="take-photo-btn"
              >
                <Camera className="w-8 h-8 mb-1" />
                <span className="text-xs font-medium">Ajouter</span>
              </button>
            </div>

            <input ref={fileInputRef} type="file" onChange={handlePhotoAdd} className="hidden" />

            {photos.length > 0 ? (
              <p className="text-xs text-emerald-600 text-center font-medium">{photos.length} photo{photos.length > 1 ? 's' : ''} ajoutée{photos.length > 1 ? 's' : ''}</p>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
                <p><strong>Conseil :</strong> Prenez vos photos avec l'appareil photo de votre téléphone, puis revenez ici et appuyez sur "Ajouter" pour les sélectionner depuis votre galerie.</p>
                <p className="text-blue-600">Vous pouvez passer cette étape et envoyer les photos plus tard à votre gestionnaire.</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Vehicles */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-vehicles">
            <div className="text-center mb-4">
              <Car className="w-10 h-10 text-[#d4a52a] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-[#1e3a5f]">Véhicules impliqués</h2>
            </div>
            <div className="space-y-2">
              <Label>Plaque de votre véhicule *</Label>
              <Input
                placeholder="AA-123-BB"
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                className="h-12 text-lg text-center font-mono tracking-wider"
                data-testid="vehicle-plate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Plaque du véhicule tiers</Label>
              <Input
                placeholder="CC-456-DD (si visible)"
                value={tpVehiclePlate}
                onChange={(e) => setTpVehiclePlate(e.target.value.toUpperCase())}
                className="h-12 text-lg text-center font-mono tracking-wider"
                data-testid="tp-vehicle-plate-input"
              />
            </div>
          </div>
        )}

        {/* Step 4: Third party */}
        {step === 4 && (
          <div className="space-y-4" data-testid="step-third-party">
            <div className="text-center mb-4">
              <User className="w-10 h-10 text-[#d4a52a] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-[#1e3a5f]">Coordonnées du tiers</h2>
              <p className="text-sm text-slate-500">La personne impliquée dans l'accident</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Prénom *</Label>
                <Input value={tpFirstName} onChange={(e) => setTpFirstName(e.target.value)} placeholder="Jean" data-testid="tp-firstname-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nom *</Label>
                <Input value={tpLastName} onChange={(e) => setTpLastName(e.target.value)} placeholder="Dupont" data-testid="tp-lastname-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email du tiers *</Label>
              <Input type="email" value={tpEmail} onChange={(e) => setTpEmail(e.target.value)} placeholder="jean.dupont@email.com" data-testid="tp-email-input" />
              <p className="text-xs text-slate-400">Un email sera envoyé au tiers pour la suite de la procédure</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Téléphone du tiers *</Label>
              <Input type="tel" value={tpPhone} onChange={(e) => setTpPhone(e.target.value)} placeholder="06 98 76 54 32" data-testid="tp-phone-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Adresse du tiers</Label>
              <Input value={tpAddress} onChange={(e) => setTpAddress(e.target.value)} placeholder="Optionnel" data-testid="tp-address-input" />
            </div>
          </div>
        )}

        {/* Step 5: Incident */}
        {step === 5 && (
          <div className="space-y-4" data-testid="step-incident">
            <div className="text-center mb-4">
              <MapPin className="w-10 h-10 text-[#d4a52a] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-[#1e3a5f]">Description de l'incident</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} data-testid="incident-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Heure</Label>
                <Input type="time" value={incidentTime} onChange={(e) => setIncidentTime(e.target.value)} data-testid="incident-time-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Lieu</Label>
              <Input value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)} placeholder="Adresse ou lieu-dit" data-testid="location-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description de l'incident *</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Décrivez ce qui s'est passé..."
                rows={4}
                data-testid="description-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes complémentaires</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Témoins, circonstances particulières..."
                rows={2}
                data-testid="notes-input"
              />
            </div>
          </div>
        )}

        {/* Step 6: Confirmation */}
        {step === 6 && (
          <div className="space-y-4" data-testid="step-confirm">
            <div className="text-center mb-4">
              <Shield className="w-10 h-10 text-[#d4a52a] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-[#1e3a5f]">Récapitulatif</h2>
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Chauffeur</span>
                  <span className="font-medium">{driverName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Société</span>
                  <span className="font-medium">{companyName}</span>
                </div>
                {driverName && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Chauffeur</span>
                    <span className="font-medium">{driverName}</span>
                  </div>
                )}
                {depotManagerName && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Chef de dépôt</span>
                    <span className="font-medium">{depotManagerName}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Constat signé</span>
                  <span className={`font-medium ${constatSigned ? 'text-emerald-600' : 'text-red-600'}`}>
                    {constatSigned ? 'Oui' : 'Non'}
                  </span>
                </div>
                <hr />
                <div className="flex justify-between">
                  <span className="text-slate-500">Votre plaque</span>
                  <span className="font-mono font-medium">{vehiclePlate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Plaque tiers</span>
                  <span className="font-mono font-medium">{tpVehiclePlate || '—'}</span>
                </div>
                <hr />
                <div className="flex justify-between">
                  <span className="text-slate-500">Tiers</span>
                  <span className="font-medium">{tpFirstName} {tpLastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Email tiers</span>
                  <span className="font-medium text-xs">{tpEmail}</span>
                </div>
                <hr />
                <div className="flex justify-between">
                  <span className="text-slate-500">Date</span>
                  <span className="font-medium">{incidentDate} à {incidentTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Photos</span>
                  <span className="font-medium">{photos.length}</span>
                </div>
              </CardContent>
            </Card>

            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-xs">
                En soumettant cette déclaration, je confirme l'absence de dommage corporel et l'exactitude des informations fournies.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>

      {/* Fixed bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 flex gap-3 z-50">
        {step > 1 && (
          <Button
            variant="outline"
            onClick={() => { setError(''); setStep(prev => prev - 1); }}
            className="h-12 px-6"
            disabled={loading}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="outline"
            onClick={() => setStarted(false)}
            className="h-12 px-6"
          >
            Annuler
          </Button>
        )}

        {step < 6 ? (
          <Button
            onClick={nextStep}
            className="flex-1 h-12 bg-[#1e3a5f] hover:bg-[#152d4a] text-white"
            disabled={loading}
            data-testid="next-step-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Suivant
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            className="flex-1 h-12 bg-[#d4a52a] hover:bg-[#c49a25] text-white text-lg"
            disabled={loading}
            data-testid="submit-declare-btn"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Envoi en cours...</>
            ) : (
              <><CheckCircle2 className="w-5 h-5 mr-2" /> Envoyer la déclaration</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DriverDeclare;
